import { useState, useEffect } from "react";
import { api } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { avatarBg } from "@/lib/avatar";
import { Search, Bot } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function PlanDateDialog({ isOpen, onClose, onSelect }) {
  const { t } = useLocale();
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    const timer = setTimeout(() => {
      api.get(`/users/date-candidates?search=${encodeURIComponent(search)}`)
        .then((r) => setCandidates(r.data.candidates || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [isOpen, search]);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col" data-testid="plan-date-dialog">
        <DialogHeader>
          <DialogTitle>{t("planDate.title")}</DialogTitle>
          <DialogDescription>{t("planDate.desc")}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("planDate.search")}
            className="pl-9"
            data-testid="plan-date-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 mt-2 pr-1">
          {loading && candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t("planDate.empty")}</p>
          ) : (
            candidates.map((c) => (
              <button
                key={c.user_id}
                onClick={() => onSelect(c)}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors text-left"
                data-testid={`plan-date-candidate-${c.user_id}`}
              >
                <img
                  src={c.photos?.[0] || c.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || "?")}&background=${avatarBg()}&color=fff&size=80`}
                  alt={c.name}
                  className="w-11 h-11 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate flex items-center gap-1.5">
                    {c.name}{c.age ? `, ${c.age}` : ""}
                    {c.is_ai && <Bot className="w-3.5 h-3.5 text-violet-400" />}
                  </p>
                  {c.location && <p className="text-xs text-muted-foreground truncate">{c.location}</p>}
                </div>
                {c.avg_rating ? <span className="text-xs text-amber-400">★ {Number(c.avg_rating).toFixed(1)}</span> : null}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
